import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getBookings, createBooking, getAgencies, getStaff, confirmBooking, reverseBooking, editBooking, deleteBooking, getDistributorBalance, exportBookingsExcel, exportBookingsPdf, partialRefundBooking } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, ShoppingCart, ArrowCounterClockwise, Warning, CopySimple, PencilSimple, Trash, CalendarBlank, CheckCircle, Wallet, ArrowDown, ArrowUp, FileXls, FilePdf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BookingWalletBar } from '../components/bookings/BookingWalletBar';
import { BookingSummaryCards } from '../components/bookings/BookingSummaryCards';
import { PartialRefundDialog } from '../components/bookings/PartialRefundDialog';

export default function BookingsPage({ embedded = false }) {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseRefundTo, setReverseRefundTo] = useState('card');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editData, setEditData] = useState({ quantity: 1, reason: '' });
  const [deleteReason, setDeleteReason] = useState('');
  const [partialRefundOpen, setPartialRefundOpen] = useState(false);
  const [prConfirmedQty, setPrConfirmedQty] = useState('');
  const [prAmazonAccount, setPrAmazonAccount] = useState('');
  const [prReason, setPrReason] = useState('');

  const todayDDMMYYYY = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' }).replace(/\//g, '-');

  const [formData, setFormData] = useState({
    staff_id: '',
    agency_id: '',
    quantity: 1,
    booking_date: todayDDMMYYYY,
    source: 'tataneu',
  });

  const isOwner = user?.role === 'admin' || user?.role === 'owner';
  const isAdmin = isOwner || user?.role === 'assistant';
  const isDistributor = user?.role === 'distributor';
  const isStaff = user?.role === 'staff';
  const isAccountant = user?.role === 'accountant';
  const isSalesPerson = user?.role === 'sales_person';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const agencyId = selectedAgency === 'all' ? undefined : selectedAgency;
      const sd = startDate || undefined;
      const ed = endDate || undefined;
      const promises = [
        getBookings(staffId, undefined, sd, ed, agencyId),
        getAgencies(),
      ];
      if (isAdmin) promises.push(getStaff());
      const results = await Promise.all(promises);
      setBookings(results[0].data);
      setAgencies(results[1].data);
      if (isAdmin && results[2]) setStaffList(results[2].data.filter(s => s.role === 'staff'));

      // Fetch wallet data (aggregate from array response) — NOT for sales person
      if (!isSalesPerson) {
        try {
          const walletRes = await getDistributorBalance();
          const balances = walletRes.data || [];
          const totalDebit = balances.reduce((s, b) => s + (b.total_debit || 0), 0);
          const totalCredit = balances.reduce((s, b) => s + (b.total_credit || 0), 0);
          setWalletData({
            total_balance: Math.round((totalCredit - totalDebit) * 100) / 100,
            total_debit: Math.round(totalDebit * 100) / 100,
            total_credit: Math.round(totalCredit * 100) / 100,
          });
        } catch (err) { console.error('Balance calc error:', err); }
      }
    } catch (err) {
      toast.error('Data load nahi hua');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isSalesPerson, selectedStaff, startDate, endDate, selectedAgency]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAgencyChange = (agencyId) => {
    setFormData({ ...formData, agency_id: agencyId });
  };

  const handleSubmit = async (e, forceDuplicate = false) => {
    if (e) e.preventDefault();
    if (!formData.agency_id) { toast.error('Agency select karein'); return; }
    if (isAdmin && !formData.staff_id) { toast.error('Staff select karein'); return; }
    if (!formData.quantity || parseInt(formData.quantity) <= 0) { toast.error('Quantity daalo'); return; }

    setSubmitting(true);
    try {
      await createBooking({
        staff_id: isAdmin ? formData.staff_id : undefined,
        agency_id: formData.agency_id,
        quantity: parseInt(formData.quantity) || 1,
        booking_date: formData.booking_date || todayDDMMYYYY,
        source: formData.source || 'cash',
        force_duplicate: forceDuplicate
      });
      toast.success('Booking created (Pending)');
      setDialogOpen(false);
      setDuplicateDialogOpen(false);
      setDuplicateInfo(null);
      setFormData({ staff_id: '', agency_id: '', quantity: 1, booking_date: todayDDMMYYYY, source: 'tataneu' });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.type === 'duplicate_warning') {
        setDuplicateInfo(detail);
        setDuplicateDialogOpen(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Booking create fail');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (bookingId) => {
    setConfirmingId(bookingId);
    try {
      const res = await confirmBooking(bookingId);
      toast.success(isStaff ? 'Booking confirmed!' : (res.data.message || 'Booking confirmed!'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirm fail');
    } finally {
      setConfirmingId(null);
    }
  };

  const openReverseDialog = (booking) => {
    setSelectedBooking(booking);
    setReverseReason('');
    setReverseRefundTo('card');
    setReverseDialogOpen(true);
  };

  const handleReverseBooking = async () => {
    if (!selectedBooking || !reverseReason.trim()) {
      toast.error('Reason zaroori hai'); return;
    }
    try {
      await reverseBooking(selectedBooking.id, { reason: reverseReason, refund_to: reverseRefundTo });
      toast.success('Booking reversed');
      setReverseDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reverse fail');
    }
  };

  const openEditDialog = (b) => {
    setSelectedBooking(b);
    setEditData({ quantity: b.quantity, reason: '' });
    setEditDialogOpen(true);
  };

  const handleEditBooking = async () => {
    if (!selectedBooking || !editData.reason.trim()) {
      toast.error('Reason daalna zaroori hai'); return;
    }
    try {
      await editBooking(selectedBooking.id, {
        quantity: parseInt(editData.quantity) || selectedBooking.quantity,
        reason: editData.reason
      });
      toast.success('Booking updated');
      setEditDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit fail');
    }
  };

  const openDeleteDialog = (b) => {
    setSelectedBooking(b);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking) return;
    try {
      await deleteBooking(selectedBooking.id, deleteReason || 'Staff correction');
      toast.success('Booking deleted');
      setDeleteDialogOpen(false);
      setSelectedBooking(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete fail');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amount || 0);

  const openPartialRefund = (b) => {
    setSelectedBooking(b);
    setPrConfirmedQty('');
    setPrAmazonAccount(b.amazon_account || '');
    setPrReason('');
    setPartialRefundOpen(true);
  };

  const handlePartialRefund = async () => {
    if (!selectedBooking || !prConfirmedQty) { toast.error('Confirmed qty enter karein'); return; }
    const confirmed = parseInt(prConfirmedQty);
    if (confirmed >= selectedBooking.quantity) { toast.error(`Confirmed qty (${confirmed}) booked qty (${selectedBooking.quantity}) se kam hona chahiye`); return; }
    setSubmitting(true);
    try {
      const res = await partialRefundBooking(selectedBooking.id, {
        confirmed_qty: confirmed,
        amazon_account: prAmazonAccount,
        reason: prReason || `Agency confirmed ${confirmed} out of ${selectedBooking.quantity}`,
      });
      toast.success(res.data.message);
      setPartialRefundOpen(false);
      setPrConfirmedQty(''); setPrAmazonAccount(''); setPrReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Partial refund failed');
    } finally { setSubmitting(false); }
  };

  const getStatusBadge = (status, isReversed) => {
    if (isReversed || status === 'reversed') return <span className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded"><ArrowCounterClockwise size={12} /> Reversed</span>;
    if (status === 'confirmed' || status === 'completed') return <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded"><CheckCircle size={12} weight="fill" /> Confirmed</span>;
    if (status === 'pending') return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">Pending</span>;
    return <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">{status}</span>;
  };

  const totalQuantity = bookings.filter(b => !b.is_reversed && b.status !== 'reversed').reduce((sum, b) => sum + (b.quantity || 1), 0);
  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const confirmedCount = bookings.filter(b => b.status === 'confirmed' || b.status === 'completed').length;

  const selectedAgencyInfo = agencies.find(a => a.id === formData.agency_id);

  return (
    <div className="space-y-5" data-testid="bookings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!embedded && <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Booking History</h1>}
          {embedded && <h2 className="text-lg font-bold text-gray-900">Booking History</h2>}
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={async () => { try { const res = await exportBookingsExcel(isAdmin && selectedStaff !== 'all' ? selectedStaff : undefined, startDate, endDate, selectedAgency !== 'all' ? selectedAgency : undefined); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'booking_history.xlsx'; a.click(); } catch (err) { toast.error('Export failed'); }}} data-testid="export-excel-btn">
              <FileXls size={14} className="text-green-600" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={async () => { try { const res = await exportBookingsPdf(isAdmin && selectedStaff !== 'all' ? selectedStaff : undefined, startDate, endDate, selectedAgency !== 'all' ? selectedAgency : undefined); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = 'booking_history.pdf'; a.click(); } catch (err) { toast.error('Export failed'); }}} data-testid="export-pdf-btn">
              <FilePdf size={14} className="text-red-600" /> PDF
            </Button>
          </div>
        </div>
        {!isDistributor && !isStaff && !isSalesPerson && !isAccountant && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 self-start sm:self-auto" data-testid="add-booking-btn">
                <Plus size={18} className="mr-2" /> New Booking
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>New Booking</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                {isAdmin && (
                  <div>
                    <Label className="text-sm font-medium">Staff <span className="text-red-500">*</span></Label>
                    <SearchableSelect
                      options={staffList.map(s => ({ value: s.id, label: s.name, sublabel: s.email }))}
                      value={formData.staff_id}
                      onValueChange={(v) => setFormData({ ...formData, staff_id: v })}
                      placeholder="Select Staff"
                      searchPlaceholder="Search staff..."
                      data-testid="booking-staff-select"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-sm font-medium">Agency <span className="text-red-500">*</span></Label>
                  <SearchableSelect
                    options={agencies.map(a => ({ value: a.id, label: a.name, sublabel: isStaff ? `Rate: ${formatCurrency(a.rate)}` : `Rate: ${formatCurrency(a.rate)} | Fee: ${formatCurrency(a.convenience_fee || 0)}/cyl` }))}
                    value={formData.agency_id}
                    onValueChange={handleAgencyChange}
                    placeholder="Select Agency"
                    searchPlaceholder="Search agency..."
                    data-testid="booking-agency-select"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Quantity <span className="text-red-500">*</span></Label>
                    <Input
                      type="number" min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      required data-testid="booking-quantity-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Booking Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      value={formData.booking_date}
                      onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })}
                      placeholder="DD-MM-YYYY"
                      required data-testid="booking-date-input"
                    />
                  </div>
                </div>

                {/* Payment Source */}
                <div>
                  <Label className="text-sm font-medium">Payment Source <span className="text-red-500">*</span></Label>
                  <div className="flex gap-2 mt-1">
                    {[
                      { value: 'tataneu', label: 'TataNeu', color: 'border-pink-400 bg-pink-50 text-pink-700' },
                      { value: 'amazon', label: 'Amazon', color: 'border-orange-400 bg-orange-50 text-orange-700' },
                      { value: 'cash', label: 'Cash', color: 'border-gray-400 bg-gray-50 text-gray-700' },
                    ].map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, source: s.value })}
                        className={`flex-1 py-2 px-3 text-sm font-medium border-2 rounded-md transition-all ${
                          formData.source === s.value ? s.color + ' ring-1 ring-offset-1' : 'border-gray-200 text-gray-400'
                        }`}
                        data-testid={`source-${s.value}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Calculation Preview */}
                {selectedAgencyInfo && formData.quantity > 0 && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-gray-500">Rate:</span><span className="font-mono">{formatCurrency(selectedAgencyInfo.rate)}/cyl</span></div>
                    {!isStaff && <div className="flex justify-between"><span className="text-gray-500">Fee:</span><span className="font-mono">{formatCurrency(selectedAgencyInfo.convenience_fee || 0)}/cyl</span></div>}
                    <div className="flex justify-between font-semibold pt-1 border-t border-gray-200">
                      <span>Total:</span>
                      <span className="font-mono text-emerald-700">
                        {isStaff
                          ? formatCurrency((selectedAgencyInfo.rate || 0) * (parseInt(formData.quantity) || 0))
                          : formatCurrency(((selectedAgencyInfo.rate || 0) * (parseInt(formData.quantity) || 0)) + ((selectedAgencyInfo.convenience_fee || 0) * (parseInt(formData.quantity) || 0)))
                        }
                      </span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">Wallet debit hoga sirf CONFIRM ke baad</p>
                  </div>
                )}

                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" disabled={submitting} data-testid="booking-submit-btn">
                  {submitting ? 'Creating...' : 'Create Booking (Pending)'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Wallet Display — NOT for sales person */}
      {walletData && !isSalesPerson && (
        <BookingWalletBar walletData={walletData} formatCurrency={formatCurrency} />
      )}

      {/* Summary Cards */}
      <BookingSummaryCards
        totalQuantity={totalQuantity}
        pendingCount={pendingCount}
        confirmedCount={confirmedCount}
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap">
        {isAdmin && (
          <SearchableSelect
            options={[{ value: 'all', label: 'All Staff' }, ...staffList.map(s => ({ value: s.id, label: s.name }))]}
            value={selectedStaff}
            onValueChange={setSelectedStaff}
            placeholder="Staff"
            className="w-full sm:w-44"
            data-testid="staff-filter-bookings"
          />
        )}
        <SearchableSelect
          options={[{ value: 'all', label: 'All Agencies' }, ...agencies.map(a => ({ value: a.id, label: a.name }))]}
          value={selectedAgency}
          onValueChange={setSelectedAgency}
          placeholder="Agency"
          className="w-full sm:w-48"
          data-testid="agency-filter-bookings"
        />
        <div className="flex items-center gap-1.5">
          <CalendarBlank size={16} className="text-gray-400 hidden sm:block" />
          <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-9 flex-1 sm:w-36 text-sm" data-testid="bookings-start-date" />
          <span className="text-gray-400 text-xs">to</span>
          <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-9 flex-1 sm:w-36 text-sm" data-testid="bookings-end-date" />
          {(startDate || endDate) && (
            <Button variant="ghost" size="sm" onClick={() => { setStartDate(''); setEndDate(''); }} className="text-xs text-gray-500 h-8 px-2">Clear</Button>
          )}
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading...</div>
        ) : (
          <>
            {/* Mobile View */}
            <div className="sm:hidden divide-y divide-gray-100">
              {bookings.length === 0 ? (
                <p className="text-center py-8 text-gray-400 text-sm">No bookings found</p>
              ) : bookings.map(b => (
                <div key={b.id} className={`p-3 ${b.is_reversed ? 'bg-purple-50' : b.status === 'pending' ? 'bg-amber-50/30' : ''}`} data-testid={`booking-row-${b.id}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{b.agency_name}</p>
                      <p className="text-[10px] text-gray-400 font-mono">{b.transaction_id}</p>
                      <p className="text-xs text-gray-500">{b.booking_date || formatDate(b.created_at)}</p>
                      {isAdmin && <p className="text-xs text-gray-500">Staff: {b.staff_name}</p>}
                    </div>
                    <div className="text-right ml-2 shrink-0">
                      <p className="text-base font-bold text-gray-900">{b.quantity} cyl</p>
                      <p className="text-xs font-mono text-gray-600">{formatCurrency(b.total_amount)}</p>
                      {getStatusBadge(b.status, b.is_reversed)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {b.source === 'tataneu' ? <span className="text-[10px] font-semibold bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">TataNeu</span>
                    : b.source === 'amazon' ? <span className="text-[10px] font-semibold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Amazon</span>
                    : <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{b.source || 'Cash'}</span>}
                    <span className="text-[10px] text-emerald-700 font-bold">💰 ₹{b.rate}/cyl</span>
                    {(b.fee_per_cylinder || 0) > 0 && <span className="text-[10px] text-gray-400">+ ₹{b.fee_per_cylinder} fee</span>}
                  </div>
                  {/* Partial Refund Info */}
                  {b.partial_refund && (
                    <div className="mt-2 text-[10px] bg-orange-50 text-orange-700 px-2 py-1.5 rounded border border-orange-200">
                      <span className="font-bold">PARTIAL REFUND</span> — {b.partial_refund.refund_qty} cyl (₹{b.partial_refund.refund_amount?.toLocaleString('en-IN')})
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-2">
                    {b.status === 'pending' && !isDistributor && !isAccountant && (
                      <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" onClick={() => handleConfirm(b.id)} disabled={confirmingId === b.id} data-testid={`confirm-booking-${b.id}`}>
                        {confirmingId === b.id ? 'Confirming...' : 'CONFIRM'}
                      </Button>
                    )}
                    {b.status === 'pending' && !isDistributor && !isAdmin && !isAccountant && (
                      <>
                        <Button size="sm" variant="outline" className="text-blue-600 text-xs h-8" onClick={() => openEditDialog(b)}>Edit</Button>
                        <Button size="sm" variant="outline" className="text-red-600 text-xs h-8" onClick={() => openDeleteDialog(b)}>Delete</Button>
                      </>
                    )}
                    {isOwner && (b.status === 'confirmed' || b.status === 'completed') && !b.is_reversed && !b.partial_refund && (
                      <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 text-xs h-8" onClick={() => openPartialRefund(b)} data-testid={`partial-refund-${b.id}`}>
                        Partial Refund
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">TXN ID</TableHead>
                    <TableHead className="text-xs">Date</TableHead>
                    {isAdmin && <TableHead className="text-xs">Staff</TableHead>}
                    <TableHead className="text-xs">Agency</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    {!isStaff && <TableHead className="text-xs text-right">Fee/Cyl</TableHead>}
                    <TableHead className="text-xs text-right">Total</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={12} className="text-center text-gray-400 py-8">No bookings found</TableCell></TableRow>
                  ) : bookings.map(b => (
                    <TableRow key={b.id} className={b.is_reversed ? 'bg-purple-50' : b.status === 'pending' ? 'bg-amber-50/30' : ''} data-testid={`booking-row-${b.id}`}>
                      <TableCell className="text-xs font-mono text-gray-500">{b.transaction_id || '-'}</TableCell>
                      <TableCell className="text-xs">{b.booking_date || formatDate(b.created_at)}</TableCell>
                      {isAdmin && <TableCell className="text-sm font-medium">{b.staff_name}</TableCell>}
                      <TableCell className="text-sm font-medium">{b.agency_name}</TableCell>
                      <TableCell>
                        {b.source === 'tataneu' ? <span className="text-[10px] font-semibold bg-pink-100 text-pink-700 px-1.5 py-0.5 rounded">TataNeu</span>
                        : b.source === 'amazon' ? <span className="text-[10px] font-semibold bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Amazon</span>
                        : <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{b.source || 'Cash'}</span>}
                      </TableCell>
                      <TableCell className="text-right font-bold text-sm">{b.quantity}</TableCell>
                      <TableCell className="text-right text-sm font-semibold text-emerald-700">{formatCurrency(b.rate)}</TableCell>
                      {!isStaff && <TableCell className="text-right text-sm">{formatCurrency(b.fee_per_cylinder || 0)}</TableCell>}
                      <TableCell className="text-right font-semibold text-sm">{isStaff ? formatCurrency((() => { const base = b.rate * b.quantity; return b.source === 'tataneu' ? Math.round((base + base * 2.065 / 100) * 100) / 100 : base; })()) : formatCurrency(b.total_amount)}</TableCell>
                      <TableCell>{getStatusBadge(b.status, b.is_reversed)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {b.status === 'pending' && !isDistributor && !isAccountant && (
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs px-3" onClick={() => handleConfirm(b.id)} disabled={confirmingId === b.id} data-testid={`confirm-booking-${b.id}`}>
                              {confirmingId === b.id ? '...' : 'CONFIRM'}
                            </Button>
                          )}
                          {b.status === 'pending' && !isDistributor && !isAdmin && !isAccountant && (
                            <>
                              <Button size="sm" variant="ghost" className="text-blue-600 h-7" onClick={() => openEditDialog(b)} data-testid={`edit-booking-${b.id}`}>
                                <PencilSimple size={14} />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-600 h-7" onClick={() => openDeleteDialog(b)} data-testid={`delete-booking-${b.id}`}>
                                <Trash size={14} />
                              </Button>
                            </>
                          )}
                          {isOwner && (b.status === 'confirmed' || b.status === 'completed') && !b.is_reversed && (
                            <>
                              {!b.partial_refund && (
                                <Button size="sm" variant="ghost" className="text-orange-600 h-7 text-xs" onClick={() => openPartialRefund(b)} data-testid={`partial-refund-${b.id}`}>
                                  Partial Refund
                                </Button>
                              )}
                            </>
                          )}
                          {/* Partial Refund Info Badge */}
                          {b.partial_refund && (
                            <div className="text-[10px] bg-orange-100 text-orange-700 px-2 py-1 rounded border border-orange-300">
                              <span className="font-bold">PARTIAL REFUND</span> — {b.partial_refund.refund_qty} cyl (₹{b.partial_refund.refund_amount?.toLocaleString('en-IN')}) by {b.partial_refund.done_by_name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Reverse Booking Dialog */}
      <AlertDialog open={reverseDialogOpen} onOpenChange={setReverseDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning size={24} className="text-orange-500" /> Reverse Booking
            </AlertDialogTitle>
            <AlertDialogDescription>
              Booking reverse karne se distributor wallet mein credit hoga: {selectedBooking && formatCurrency(selectedBooking.total_amount)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 my-4">
            <div>
              <Label className="text-sm font-medium">Refund To</Label>
              <Select value={reverseRefundTo} onValueChange={setReverseRefundTo}>
                <SelectTrigger data-testid="reverse-refund-to-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="card">Card Balance</SelectItem>
                  <SelectItem value="amazon">Amazon Balance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Reason <span className="text-red-500">*</span></Label>
              <Textarea value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} placeholder="Reason for reversal..." required data-testid="reverse-reason-input" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReverseBooking} className="bg-purple-600 hover:bg-purple-700" data-testid="confirm-reverse-btn">Reverse Booking</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Warning Dialog */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <CopySimple size={24} weight="fill" /> Duplicate Detected
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>{duplicateInfo?.message}</p>
                <div className="bg-amber-50 border border-amber-200 p-3 text-sm">
                  <p><strong>Type:</strong> {duplicateInfo?.agency_type}</p>
                  <p><strong>Qty:</strong> {duplicateInfo?.quantity}</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleSubmit(null, true)} className="bg-amber-600 hover:bg-amber-700" data-testid="duplicate-continue-btn">Continue Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Booking Dialog */}
      <AlertDialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><PencilSimple size={24} className="text-blue-600" /> Edit Booking</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 pt-2">
                <div className="bg-blue-50 border border-blue-200 p-3 text-sm rounded">
                  <p><strong>Agency:</strong> {selectedBooking?.agency_name}</p>
                  <p><strong>Current Qty:</strong> {selectedBooking?.quantity}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Quantity</Label>
                  <Input type="number" min="1" value={editData.quantity} onChange={(e) => setEditData({ ...editData, quantity: e.target.value })} data-testid="edit-booking-qty" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Reason *</Label>
                  <Input value={editData.reason} onChange={(e) => setEditData({ ...editData, reason: e.target.value })} placeholder="Wrong quantity..." data-testid="edit-booking-reason" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleEditBooking} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-booking-save-btn">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Booking Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><Trash size={24} /> Delete Booking?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <div className="bg-red-50 border border-red-200 p-3 text-sm rounded">
                  <p><strong>Agency:</strong> {selectedBooking?.agency_name}</p>
                  <p><strong>Qty:</strong> {selectedBooking?.quantity}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Reason (optional)</Label>
                  <Input value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Wrong entry..." data-testid="delete-booking-reason" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBooking} className="bg-red-600 hover:bg-red-700" data-testid="delete-booking-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Partial Refund Dialog */}
      <PartialRefundDialog
        open={partialRefundOpen}
        onOpenChange={setPartialRefundOpen}
        selectedBooking={selectedBooking}
        prConfirmedQty={prConfirmedQty}
        setPrConfirmedQty={setPrConfirmedQty}
        prAmazonAccount={prAmazonAccount}
        setPrAmazonAccount={setPrAmazonAccount}
        prReason={prReason}
        setPrReason={setPrReason}
        submitting={submitting}
        handlePartialRefund={handlePartialRefund}
      />
    </div>
  );
}
